import "@/App.css";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import AppShell from "./components/AppShell";
import { CookieBanner } from "./components/CookieBanner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PlanProvider, usePlan } from "./context/PlanContext";
import { ShopfloorIdentityProvider } from "./context/ShopfloorIdentityContext";
import { homePath } from "./components/navigation";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import { COOKIE_POLICY, DPA, PRIVACY_POLICY, SECURITY_BACKUP, SLA, TERMS } from "./lib/legalContent";
import "@/styles/platform.css";
import "@/styles/public.css";

const LegalPage = lazy(() => import("./pages/LegalPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const RegisterCompanyPage = lazy(() => import("./pages/RegisterCompanyPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ResendVerificationPage = lazy(() => import("./pages/ResendVerificationPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ConfirmEmailChangePage = lazy(() => import("./pages/ConfirmEmailChangePage"));
const ActivateInvitationPage = lazy(() => import("./pages/ActivateInvitationPage"));
const QuotePrintPage = lazy(() => import("./pages/QuotePrintPage"));
const IntroPreviewPage = lazy(() => import("./pages/IntroPreviewPage"));

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ToolCatalogPage = lazy(() => import("./pages/ToolCatalogPage"));
const ToolDetailPage = lazy(() => import("./pages/ToolDetailPage"));
const BaseAttachmentsPage = lazy(() => import("./pages/BaseAttachmentsPage"));
const MaterialsPage = lazy(() => import("./pages/MaterialsPage"));
const PartsPage = lazy(() => import("./pages/PartsPage").then((module) => ({ default: module.PartsPage })));
const PartDetailPage = lazy(() => import("./pages/PartsPage").then((module) => ({ default: module.PartDetailPage })));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const JobsPage = lazy(() => import("./pages/JobsPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const ProductionOrdersPage = lazy(() => import("./pages/ProductionOrdersPage"));
const ProductionOrderDetailPage = lazy(() => import("./pages/ProductionOrderDetailPage"));
const LotsPage = lazy(() => import("./pages/LotsPage"));
const DeliveriesPage = lazy(() => import("./pages/DeliveriesPage"));
const KanbanPage = lazy(() => import("./pages/KanbanPage"));
const PhasesTablePage = lazy(() => import("./pages/PhasesTablePage"));
const ShopFloorPage = lazy(() => import("./pages/ShopFloorPage"));
const PlanningDashboardPage = lazy(() => import("./pages/PlanningDashboardPage"));
const ProductionPlannerPage = lazy(() => import("./pages/ProductionPlannerPage"));
const ProductionAuditLogPage = lazy(() => import("./pages/ProductionAuditLogPage"));
const JobClosureReportPage = lazy(() => import("./pages/JobClosureReportPage"));
const PhaseEfficiencyPage = lazy(() => import("./pages/PhaseEfficiencyPage"));
const QualityProceduresPage = lazy(() => import("./pages/QualityProceduresPage"));
const QualityProcedureDetailPage = lazy(() => import("./pages/QualityProcedureDetailPage"));
const QualityControlPlansPage = lazy(() => import("./pages/QualityControlPlansPage"));
const QualityControlPlanDetailPage = lazy(() => import("./pages/QualityControlPlanDetailPage"));
const QualityMeasurementsPage = lazy(() => import("./pages/QualityMeasurementsPage"));
const QualityInspectionsPage = lazy(() => import("./pages/QualityInspectionsPage"));
const QualityInspectionDetailPage = lazy(() => import("./pages/QualityInspectionDetailPage"));
const QualityDashboardPage = lazy(() => import("./pages/QualityDashboardPage"));
const QualityNonConformitiesPage = lazy(() => import("./pages/QualityNonConformitiesPage"));
const QualityCorrectiveActionsPage = lazy(() => import("./pages/QualityCorrectiveActionsPage"));
const QualityManagementReviewPage = lazy(() => import("./pages/QualityManagementReviewPage"));
const QualityAuditLogPage = lazy(() => import("./pages/QualityAuditLogPage"));
const MachinesPage = lazy(() => import("./pages/MachinesPage"));
const DepartmentsPage = lazy(() => import("./pages/DepartmentsPage"));
const RawFormatsPage = lazy(() => import("./pages/RawFormatsPage"));
const MachineComparisonPage = lazy(() => import("./pages/MachineComparisonPage"));
const MachineDetailPage = lazy(() => import("./pages/MachineDetailPage"));
const SitesPage = lazy(() => import("./pages/SitesPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const FixturesPage = lazy(() => import("./pages/FixturesPage"));
const PhaseFixturesPage = lazy(() => import("./pages/PhaseFixturesPage"));
const WipTagsPage = lazy(() => import("./pages/WipTagsPage"));
const ScanQrPage = lazy(() => import("./pages/ScanQrPage"));
const WipTagDetailPage = lazy(() => import("./pages/WipTagDetailPage"));
const WarehousesPage = lazy(() => import("./pages/WarehousesPage"));
const MovementsPage = lazy(() => import("./pages/MovementsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const ConsumptionKpiPage = lazy(() => import("./pages/ConsumptionKpiPage"));
const PurchaseOrdersPage = lazy(() => import("./pages/PurchaseOrdersPage"));
const PurchaseOrderDetailPage = lazy(() => import("./pages/PurchaseOrderDetailPage"));
const PurchasingDashboardPage = lazy(() => import("./pages/PurchasingDashboardPage"));
const PurchaseProposalsPage = lazy(() => import("./pages/PurchaseProposalsPage"));
const PurchaseItemsPage = lazy(() => import("./pages/PurchaseItemsPage"));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const SupplierDetailPage = lazy(() => import("./pages/SupplierDetailPage"));
const CommunicationPage = lazy(() => import("./pages/CommunicationPage"));
const CompanyInfoPage = lazy(() => import("./pages/CompanyInfoPage"));
const UserSettingsPage = lazy(() => import("./pages/UserSettingsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const BackupPage = lazy(() => import("./pages/BackupPage"));
const PlanPage = lazy(() => import("./pages/PlanPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const NotificationPreferencesPage = lazy(() => import("./pages/NotificationPreferencesPage"));
const QuotesPage = lazy(() => import("./pages/QuotesPage"));
const QuoteDetailPage = lazy(() => import("./pages/QuoteDetailPage"));
const QuoteSettingsPage = lazy(() => import("./pages/QuoteSettingsPage"));
const ControllingCostConfigPage = lazy(() => import("./pages/ControllingCostConfigPage"));
const ControllingDashboardPage = lazy(() => import("./pages/ControllingDashboardPage"));
const ControllingMarginReportPage = lazy(() => import("./pages/ControllingMarginReportPage"));
const ControllingCustomerReportPage = lazy(() => import("./pages/ControllingCustomerReportPage"));
const CustomerCardPage = lazy(() => import("./pages/CustomerCardPage"));
const ControllingProductionReportPage = lazy(() => import("./pages/ControllingProductionReportPage"));
const ControllingQualityReportPage = lazy(() => import("./pages/ControllingQualityReportPage"));
const ControllingJobPricesPage = lazy(() => import("./pages/ControllingJobPricesPage"));
const StampPhasePage = lazy(() => import("./pages/StampPhasePage"));
const MyDayPlanPage = lazy(() => import("./pages/MyDayPlanPage"));
const ShiftsPage = lazy(() => import("./pages/ShiftsPage"));
const ShopBoardPage = lazy(() => import("./pages/ShopBoardPage"));

function PageFallback() {
  return <div className="auth-loading" data-testid="page-loading">Caricamento...</div>;
}

function LegacyOrderRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/ordini-cliente/${id}` : "/ordini-cliente"} replace />;
}

function HomeRedirect() {
  const { session } = useAuth();
  const { plan } = usePlan();
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaited(true), 1500);
    return () => clearTimeout(timer);
  }, []);
  if (!plan && !waited) return <div className="auth-loading" data-testid="home-redirect-loading">Apertura area aziendale...</div>;
  return <Navigate to={homePath(session.user.permissions, plan?.modules || [])} replace />;
}

function NotFoundRoute() {
  const location = useLocation();
  return (
    <section className="plan-locked" data-testid="route-not-found">
      <p className="eyebrow">INDIRIZZO NON VALIDO</p>
      <h2>Questa pagina non esiste</h2>
      <p className="plan-locked-text">L'indirizzo <b>{location.pathname}</b> non corrisponde a nessuna pagina dell'applicazione. Può essere un collegamento vecchio o un indirizzo digitato a mano: usa il menu di sinistra oppure torna all'area aziendale.</p>
      <Link className="primary-button" to="/" data-testid="route-not-found-home">Torna all'area aziendale</Link>
    </section>
  );
}

function ProtectedApp() {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="auth-loading" data-testid="auth-loading">Caricamento area aziendale...</div>;
  if (!session) return location.pathname === "/" ? <LandingPage /> : <Navigate to="/login" replace />;
  return (
    <PlanProvider>
      <AppShell>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/quadro-utensili" element={<DashboardPage />} />
            <Route path="/controllo/dashboard" element={<ControllingDashboardPage />} />
            <Route path="/controllo/marginalita" element={<ControllingMarginReportPage />} />
            <Route path="/controllo/clienti" element={<ControllingCustomerReportPage />} />
            <Route path="/controllo/produzione" element={<ControllingProductionReportPage />} />
            <Route path="/controllo/qualita" element={<ControllingQualityReportPage />} />
            <Route path="/controllo/prezzi" element={<ControllingJobPricesPage />} />
            <Route path="/controllo/configurazione" element={<ControllingCostConfigPage />} />
            <Route path="/utensili" element={<ToolCatalogPage />} />
            <Route path="/utensili/:id" element={<ToolDetailPage />} />
            <Route path="/attacchi-base" element={<BaseAttachmentsPage />} />
            <Route path="/materiali" element={<MaterialsPage />} />
            <Route path="/componenti" element={<PartsPage />} />
            <Route path="/componenti/:id" element={<PartDetailPage />} />
            <Route path="/pezzi" element={<PartsPage />} />
            <Route path="/pezzi/:id" element={<PartDetailPage />} />
            <Route path="/clienti" element={<CustomersPage />} />
            <Route path="/clienti/:id" element={<CustomerCardPage />} />
            <Route path="/ordini-cliente" element={<JobsPage />} />
            <Route path="/ordini-cliente/:id" element={<JobDetailPage />} />
            <Route path="/commesse" element={<Navigate to="/ordini-cliente" replace />} />
            <Route path="/commesse/:id" element={<LegacyOrderRedirect />} />
            <Route path="/progetti" element={<ProjectsPage />} />
            <Route path="/progetti/:id" element={<ProjectDetailPage />} />
            <Route path="/produzione/ordini" element={<ProductionOrdersPage />} />
            <Route path="/produzione/ordini/:id" element={<ProductionOrderDetailPage />} />
            <Route path="/produzione/lotti" element={<LotsPage />} />
            <Route path="/consegne" element={<DeliveriesPage />} />
            <Route path="/produzione/kanban" element={<KanbanPage />} />
            <Route path="/produzione/fasi" element={<PhasesTablePage />} />
            <Route path="/produzione/officina" element={<ShopFloorPage />} />
            <Route path="/officina/scansiona" element={<ScanQrPage />} />
            <Route path="/produzione/dashboard" element={<PlanningDashboardPage />} />
            <Route path="/produzione/pianificazione" element={<ProductionPlannerPage />} />
            <Route path="/produzione/storico" element={<ProductionAuditLogPage />} />
            <Route path="/produzione/report-chiusura" element={<JobClosureReportPage />} />
            <Route path="/produzione/efficienza-fasi" element={<PhaseEfficiencyPage />} />
            <Route path="/qualita/procedure" element={<QualityProceduresPage />} />
            <Route path="/qualita/procedure/:id" element={<QualityProcedureDetailPage />} />
            <Route path="/qualita/piani-controllo" element={<QualityControlPlansPage />} />
            <Route path="/qualita/piani-controllo/:id" element={<QualityControlPlanDetailPage />} />
            <Route path="/qualita/strumenti" element={<QualityMeasurementsPage />} />
            <Route path="/qualita/controlli" element={<QualityInspectionsPage />} />
            <Route path="/qualita/controlli/:id" element={<QualityInspectionDetailPage />} />
            <Route path="/qualita/dashboard" element={<QualityDashboardPage />} />
            <Route path="/qualita/non-conformita" element={<QualityNonConformitiesPage />} />
            <Route path="/qualita/azioni-correttive" element={<QualityCorrectiveActionsPage />} />
            <Route path="/qualita/report-direzione" element={<QualityManagementReviewPage />} />
            <Route path="/qualita/audit" element={<QualityAuditLogPage />} />
            <Route path="/preventivi" element={<QuotesPage />} />
            <Route path="/preventivi/impostazioni" element={<QuoteSettingsPage />} />
            <Route path="/preventivi/:id" element={<QuoteDetailPage />} />
            <Route path="/macchine" element={<MachinesPage />} />
            <Route path="/reparti" element={<DepartmentsPage />} />
            <Route path="/turni" element={<ShiftsPage />} />
            <Route path="/formati-grezzo" element={<RawFormatsPage />} />
            <Route path="/macchine/confronto" element={<MachineComparisonPage />} />
            <Route path="/macchine/:id" element={<MachineDetailPage />} />
            <Route path="/sedi" element={<SitesPage />} />
            <Route path="/inventario" element={<InventoryPage />} />
            <Route path="/attrezzature-staffaggio" element={<FixturesPage />} />
            <Route path="/attrezzature-staffaggio/assegnazione" element={<PhaseFixturesPage />} />
            <Route path="/semilavorati" element={<WipTagsPage />} />
            <Route path="/semilavorati/cartellino/:tagId" element={<WipTagDetailPage />} />
            <Route path="/magazzini" element={<WarehousesPage />} />
            <Route path="/movimenti" element={<MovementsPage />} />
            <Route path="/analisi" element={<AnalyticsPage />} />
            <Route path="/consumo-utensili" element={<ConsumptionKpiPage />} />
            <Route path="/acquisti" element={<Navigate to="/acquisti/dashboard" replace />} />
            <Route path="/acquisti/dashboard" element={<PurchasingDashboardPage />} />
            <Route path="/acquisti/proposte" element={<PurchaseProposalsPage />} />
            <Route path="/acquisti/ordini" element={<PurchaseOrdersPage />} />
            <Route path="/acquisti/ordini/:orderId" element={<PurchaseOrderDetailPage />} />
            <Route path="/acquisti/fornitori" element={<SuppliersPage />} />
            <Route path="/acquisti/fornitori/:supplierId" element={<SupplierDetailPage />} />
            <Route path="/acquisti/articoli" element={<PurchaseItemsPage />} />
            <Route path="/comunicazione" element={<CommunicationPage />} />
            <Route path="/azienda" element={<CompanyInfoPage />} />
            <Route path="/impostazioni-utente" element={<UserSettingsPage />} />
            <Route path="/utenti" element={<UsersPage />} />
            <Route path="/sicurezza" element={<BackupPage />} />
            <Route path="/piano" element={<PlanPage />} />
            <Route path="/super-admin" element={<SuperAdminPage />} />
            <Route path="/preferenze-avvisi" element={<NotificationPreferencesPage />} />
            <Route path="*" element={<NotFoundRoute />} />
          </Routes>
        </Suspense>
      </AppShell>
    </PlanProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ShopfloorIdentityProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/registra-azienda" element={<RegisterCompanyPage />} />
            <Route path="/verifica-email" element={<VerifyEmailPage />} />
            <Route path="/reinvia-conferma" element={<ResendVerificationPage />} />
            <Route path="/recupera-password" element={<ForgotPasswordPage />} />
            <Route path="/reimposta-password" element={<ResetPasswordPage />} />
            <Route path="/conferma-nuova-email" element={<ConfirmEmailChangePage />} />
            <Route path="/attiva-invito" element={<ActivateInvitationPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/soluzioni/:slug" element={<SolutionPage />} />
            <Route path="/legale/privacy" element={<LegalPage doc={PRIVACY_POLICY} />} />
            <Route path="/legale/cookie" element={<LegalPage doc={COOKIE_POLICY} />} />
            <Route path="/legale/termini" element={<LegalPage doc={TERMS} />} />
            <Route path="/legale/dpa" element={<LegalPage doc={DPA} />} />
            <Route path="/legale/sla" element={<LegalPage doc={SLA} />} />
            <Route path="/legale/sicurezza" element={<LegalPage doc={SECURITY_BACKUP} />} />
            <Route path="/preventivi/:id/stampa" element={<QuotePrintPage />} />
            <Route path="/anteprima-accesso" element={<IntroPreviewPage />} />
            <Route path="/officina/timbra/fase/:partPhaseId" element={<StampPhasePage />} />
            <Route path="/officina/timbra/odp/:plannedPhaseId" element={<StampPhasePage />} />
            <Route path="/officina/piano" element={<MyDayPlanPage />} />
            <Route path="/produzione/tabellone" element={<ShopBoardPage />} />
            <Route path="/*" element={<ProtectedApp />} />
          </Routes>
        </Suspense>
        </ShopfloorIdentityProvider>
        <Toaster theme="dark" richColors position="top-center" />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
